import React, { useState, useMemo, useCallback } from 'react';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { Checkbox } from 'antd';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import SearchField from 'components/SearchField/SearchField';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { ISelectOption } from 'components/Select/types';
import style from './MultipleSelect.module.scss';

interface IProps {
	setIDsRestaurant: (value: string[]) => void;
	restaurants: IItemForGetShortInfoWithStatus[];
	IDsSelectedRestaurant: string[];
	typeOfChanges: 'basedChanges' | 'independentChanges';
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({ setIDsRestaurant, restaurants, IDsSelectedRestaurant, typeOfChanges }) => {
		const [searchString, setSearchString] = useState<string>('');
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
		);

		const visibleData = useMemo(() => {
			return showArchived
				? restaurants
				: restaurants.filter(
						(el) => el.status === EntityStatus.Active || IDsSelectedRestaurant.includes(el.id),
				  );
		}, [restaurants, showArchived, IDsSelectedRestaurant]);

		const filteredData = useMemo(() => {
			return searchString
				? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: visibleData;
		}, [searchString, visibleData]);

		const mappedOptions = useMemo<ISelectOption[]>(() => {
			return filteredData.map((el) => ({
				...el,
				additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [filteredData]);

		const handleSelectAllData = useCallback(() => {
			if (visibleData.length === IDsSelectedRestaurant.length) {
				setIDsRestaurant([]);
			} else {
				const IDsOfAllRestaurants = visibleData.map((el) => el.id);
				setIDsRestaurant(IDsOfAllRestaurants);
			}
		}, [visibleData, IDsSelectedRestaurant, setIDsRestaurant]);

		const toggleShowArchived = useCallback(() => {
			localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		}, [showArchived]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				return (
					<>
						<div className={style.selectMenuHeader}>
							<Checkbox
								className={style.checkBox}
								checked={!!(visibleData.length && visibleData.length === IDsSelectedRestaurant.length)}
								disabled={!visibleData.length}
								onChange={handleSelectAllData}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllRestaurantsLabel"
									asFragment={true}
								/>
							</Checkbox>
							<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
								<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
							</Checkbox>
							<SearchField
								onChangeSelectSearchString={setSearchString}
								searchString={searchString}
								className={style.searchField}
							/>
						</div>
						{menu}
					</>
				);
			},
			[
				visibleData,
				IDsSelectedRestaurant,
				handleSelectAllData,
				searchString,
				setSearchString,
				showArchived,
				toggleShowArchived,
			],
		);

		return (
			<OutsideFormSelect
				testId="RestaurantsMultipleSelect"
				name="IDsSelectedRestaurant"
				label={typeOfChanges === 'basedChanges' ? 'selectOtherRestaurantsLabel' : 'restaurantsLabel'}
				value={IDsSelectedRestaurant}
				options={mappedOptions}
				onChange={setIDsRestaurant}
				mode="multiple"
				counterValue={IDsSelectedRestaurant.length}
				dropdownRender={renderSelectMenu}
			/>
		);
	},
);
